import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionableNotification,
  InlineNotification,
  MenuButton,
  MenuItem,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';
import request from 'lib/request';
import { CreateWorkspaceModal } from 'pages/well-workspaces/create-workspace-modal';
import { useCreateWorkspace } from 'pages/well-workspaces/hooks';
import {
  wellWorkspacePath,
  type WellWorkspaceLocationState,
} from 'pages/well-workspaces/utils';
import { format0 } from 'utils/number';
import { useDatasetAtoms } from './dataset-context';
import styles from './create-workspace-menu.module.scss';

const WELLS_QUERY_PATH = '/queries/wells';
const DCA_WORKSPACES_PATH = '/dca_workspaces';

type FilteredUwi = { uwi: string };
type WellsQueryResponse = {
  filteredUwis?: FilteredUwi[];
  totalCount?: number;
};
type DcaWorkspaceCreated = { href: string };

/**
 * Resolves the UWIs of every producing well in the current Map Query wells
 * selection, ignoring pagination.
 *
 * `totalCount` is how many wells matched. The backend caps how many UWIs it
 * returns, so a total higher than the number of UWIs means the list was cut
 * short.
 *
 * @param {Record<string, unknown>} committedPayload - The Wells dataset's applied search payload.
 * @param {AbortController} controller - Abort controller for the request.
 * @return {Promise<{uwis: string[], totalCount: number} | null>} The matching
 *   producing UWIs and how many matched, or null if the request was aborted.
 */
async function fetchProducingUwis(
  committedPayload: Record<string, unknown>,
  controller: AbortController
): Promise<{ uwis: string[]; totalCount: number } | null> {
  const response = await request.post<WellsQueryResponse, { error?: string }>(
    WELLS_QUERY_PATH,
    {
      ...committedPayload,
      // The UWIs come from the aggregation, not the hits, so one hit is enough.
      page: 1,
      pageSize: 1,
      use_elastic: true,
      include_filtered_uwis: true,
      // A DCA workspace only ever holds producing wells, whatever the map shows.
      status_ids: ['producing'],
    },
    controller
  );

  if (response.type === 'abort') return null;

  if (response.type !== 'success') {
    throw new Error(
      response.type === 'error' && response.data?.error
        ? response.data.error
        : 'Could not resolve wells for this selection'
    );
  }

  return {
    uwis: (response.data.filteredUwis ?? []).map((well) => well.uwi),
    totalCount: response.data.totalCount ?? 0,
  };
}

/**
 * Create menu for the Map Query results toolbar. One item makes a DCA Workspace from
 * the producing wells matching the current filters; the other opens the Well
 * Workspace modal over the same filtered wells, and the new workspace then opens at
 * its own URL like every workspace here does.
 *
 * The filters come from the dataset this is mounted under, and are sent to
 * the wells query, so it only works for the wells dataset.
 *
 * @return {JSX.Element} The Create menu plus the result notification.
 */
export const CreateWorkspaceMenu = () => {
  const wellsSearch = useAtomValue(useDatasetAtoms().searchAtom);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    href: string;
    wellCount: number;
    totalCount: number;
    truncated: boolean;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const history = useHistory<WellWorkspaceLocationState>();
  // Mounted only while open so its candidate-well read is not held behind the page.
  const [wellWorkspaceOpen, setWellWorkspaceOpen] = useState(false);

  // Create Well (Epic 3 Story 1): a workspace holding one well the operator authors. There is
  // nothing to pick, so it posts straight away and opens the workspace — no candidate modal.
  const blankWell = useCreateWorkspace((workspace) =>
    history.push(wellWorkspacePath(workspace.id), { created: true })
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleCreateDcaWorkspace = useCallback(async () => {
    const committedPayload = wellsSearch.currentPayload as
      Record<string, unknown> | undefined;
    if (!committedPayload) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const resolved = await fetchProducingUwis(committedPayload, controller);
      if (resolved === null) return;
      const { uwis, totalCount } = resolved;

      if (uwis.length === 0) {
        setError('No producing wells in the current selection');
        return;
      }

      const created = await request.post<
        DcaWorkspaceCreated,
        { errors?: Array<{ message?: string }> }
      >(
        DCA_WORKSPACES_PATH,
        { dcaWorkspace: { source: 'map_query', uwis } },
        controller
      );

      if (created.type === 'abort') return;

      if (created.type !== 'success') {
        const messages =
          created.type === 'error'
            ? created.data?.errors?.map((e) => e.message).filter(Boolean)
            : undefined;
        throw new Error(
          messages?.length ? messages.join(', ') : 'Failed to create'
        );
      }

      // The notification's action opens the workspace. Opening a tab here
      // instead would be blocked, because the two requests above outlast the
      // user activation from the click that started them.
      setResult({
        href: created.data.href,
        wellCount: uwis.length,
        totalCount,
        truncated: totalCount > uwis.length,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      // Leave the loading state alone after an abort: either a newer run set
      // it, or the menu is gone.
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [wellsSearch.currentPayload]);

  return (
    <div className={styles.container}>
      <MenuButton
        label={loading || blankWell.saving ? 'Creating...' : 'Create'}
        kind="tertiary"
        size="lg"
        // Plain bottom alignment is the one Carbon sizes to the trigger, so the menu
        // is exactly as wide as the button and sits squarely under it.
        menuAlignment="bottom"
        // Not gated on the wells payload any more: Create Well authors a blank well and
        // inherits nothing from the current filters, so it works before a search has been
        // committed. The two items that DO read the payload are disabled individually.
        disabled={loading || blankWell.saving}
      >
        {/* First, as the mock has it — the only item that creates a record rather than a
            workspace over records that already exist. */}
        <MenuItem
          label="Well"
          disabled={blankWell.saving}
          onClick={() => {
            blankWell.reset();
            blankWell.create({ createBlankWell: true });
          }}
        />
        <MenuItem
          label="DCA Workspace"
          disabled={!wellsSearch.currentPayload}
          onClick={() => handleCreateDcaWorkspace()}
        />
        <MenuItem
          label="Well Workspace"
          disabled={!wellsSearch.currentPayload}
          onClick={() => setWellWorkspaceOpen(true)}
        />
      </MenuButton>
      {wellWorkspaceOpen && (
        <CreateWorkspaceModal
          open
          kind="property"
          query={wellsSearch.currentPayload ?? {}}
          onClose={() => setWellWorkspaceOpen(false)}
          onCreated={(id) => {
            setWellWorkspaceOpen(false);
            history.push(wellWorkspacePath(id), { created: true });
          }}
        />
      )}
      {(error || result || blankWell.error) && (
        <div className={styles.notification}>
          {error && (
            <InlineNotification
              kind="error"
              title={error}
              lowContrast
              onCloseButtonClick={() => setError(null)}
            />
          )}
          {blankWell.error && (
            <InlineNotification
              kind="error"
              title="Could not create the well"
              subtitle={blankWell.error}
              lowContrast
              onCloseButtonClick={() => blankWell.reset()}
            />
          )}
          {result && (
            <ActionableNotification
              inline
              kind={result.truncated ? 'warning' : 'success'}
              title={
                result.truncated
                  ? 'Workspace created'
                  : `Workspace created with ${format0(
                      result.wellCount
                    )} producing wells`
              }
              subtitle={
                result.truncated
                  ? `There were ${format0(
                      result.totalCount
                    )} producing wells that matched your current filters, but only ${format0(
                      result.wellCount
                    )} could be added.`
                  : undefined
              }
              actionButtonLabel="Open workspace"
              onActionButtonClick={() => window.open(result.href, '_blank')}
              lowContrast
              onCloseButtonClick={() => setResult(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};
